/**
 * EquipmentAttachmentSystem — resolve os sockets do rig (RightHand/LeftHand/Back/Head/Chest,
 * §4) procurando bones por nome (candidatos, case-insensitive/substring) e prende meshes
 * reais de equipamento a eles. Trocar a arma equipada troca o mesh no socket (a arma "muda
 * visualmente quando equipada").
 *
 * Se um bone não é encontrado, cria um nó de fallback preso à raiz na posição configurada —
 * assim `attach` nunca quebra (a arma aparece, ainda que sem seguir a mão perfeitamente).
 */
import * as THREE from "three";
import type { AttachOffset, SocketConfig, SocketName } from "./ModelRegistry";

function isDev(): boolean {
  try {
    return !!import.meta.env && import.meta.env.DEV;
  } catch {
    return false;
  }
}

interface Socket {
  parent: THREE.Object3D;
  attached: THREE.Object3D | null;
  resolvedBone: string | null;
}

/** Acha o primeiro bone/objeto cujo nome casa (substring, case-insensitive) um candidato. */
function findBone(root: THREE.Object3D, candidates: string[]): THREE.Object3D | null {
  const needles = candidates.map((c) => c.toLowerCase());
  let match: THREE.Object3D | null = null;
  root.traverse((o) => {
    if (match || !o.name) return;
    const n = o.name.toLowerCase();
    if (needles.some((needle) => n.includes(needle))) match = o;
  });
  return match;
}

export class EquipmentAttachmentSystem {
  private readonly sockets = new Map<SocketName, Socket>();

  constructor(root: THREE.Object3D, configs?: Partial<Record<SocketName, SocketConfig>>) {
    if (!configs) return;
    for (const name of Object.keys(configs) as SocketName[]) {
      const cfg = configs[name]!;
      let parent = findBone(root, cfg.boneNames);
      let resolvedBone = parent?.name ?? null;
      if (!parent) {
        // fallback: nó ancorado à raiz na posição configurada (não segue a mão, mas existe)
        parent = new THREE.Group();
        parent.name = `socket_${name}_fallback`;
        if (cfg.position) parent.position.set(...cfg.position);
        root.add(parent);
        resolvedBone = null;
        if (isDev()) console.warn(`[Sockets] "${name}": bone não encontrado (${cfg.boneNames.join(", ")}) → âncora de fallback.`);
      }
      this.sockets.set(name, { parent, attached: null, resolvedBone });
    }
  }

  has(name: SocketName): boolean {
    return this.sockets.has(name);
  }

  worldPosition(name: SocketName, out: THREE.Vector3): boolean {
    const socket = this.sockets.get(name);
    if (!socket) return false;
    socket.parent.getWorldPosition(out);
    return true;
  }

  /** Prende um objeto (ex.: mesh de arma) ao socket, trocando o anterior. Aplica ajuste. */
  attach(name: SocketName, object: THREE.Object3D, cfg?: AttachOffset): void {
    const socket = this.sockets.get(name);
    if (!socket) {
      if (isDev()) console.warn(`[Sockets] attach em socket inexistente "${name}".`);
      return;
    }
    this.detach(name);
    if (cfg?.position) object.position.set(...cfg.position);
    if (cfg?.rotationEuler) object.rotation.set(...cfg.rotationEuler);
    if (cfg?.scale !== undefined) object.scale.setScalar(cfg.scale);
    socket.parent.add(object);
    socket.attached = object;
  }

  detach(name: SocketName): void {
    const socket = this.sockets.get(name);
    if (!socket?.attached) return;
    socket.parent.remove(socket.attached);
    socket.attached.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) m.geometry?.dispose?.();
    });
    socket.attached = null;
  }

  /** Objeto atualmente preso a um socket (ou null). */
  attachedAt(name: SocketName): THREE.Object3D | null {
    return this.sockets.get(name)?.attached ?? null;
  }

  /** Relatório de resolução dos sockets (bone real vs. fallback) — para validação. */
  report(): { socket: SocketName; bone: string | null }[] {
    return [...this.sockets.entries()].map(([socket, s]) => ({ socket, bone: s.resolvedBone }));
  }

  dispose(): void {
    for (const name of this.sockets.keys()) this.detach(name);
    this.sockets.clear();
  }
}
